/**
 * CodeForge - The Mecha-Garage for Young Hackers
 * ================================================
 * Hey Frick & Frack! This is the main App component.
 * It's like the control room of your spaceship!
 */

import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Import Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import SitesPage from "./pages/SitesPage";
import SiteDetailPage from "./pages/SiteDetailPage";
import FileManagerPage from "./pages/FileManagerPage";
import MLStudioPage from "./pages/MLStudioPage";
import SettingsPage from "./pages/SettingsPage";
import TutorialsPage from "./pages/TutorialsPage";
import FamilyGuidePage from "./pages/FamilyGuidePage";

import "./App.css";

// Backend URL from environment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("codeforge_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("codeforge_token");
      localStorage.removeItem("codeforge_user");
      window.location.href = "/auth";
    }
    return Promise.reject(error);
  }
);

// Export API for other components
export { api, API };

// Auth Context - Like a shared brain for user info!
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const token = localStorage.getItem("codeforge_token");
    const savedUser = localStorage.getItem("codeforge_user");
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      api.get("/auth/me")
        .then((res) => {
          setUser(res.data);
          localStorage.setItem("codeforge_user", JSON.stringify(res.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem("codeforge_token", res.data.access_token);
      localStorage.setItem("codeforge_user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      toast.success(`Welcome back, ${res.data.user.username}!`, {
        description: `+${10} XP for logging in! Streak: ${res.data.user.streak} days`,
      });
      return res.data;
    } catch (error) {
      const msg = error.response?.data?.detail || "Login failed";
      toast.error(msg);
      throw error;
    }
  };

  const register = async (username, email, password) => {
    try {
      const res = await api.post("/auth/register", { username, email, password });
      localStorage.setItem("codeforge_token", res.data.access_token);
      localStorage.setItem("codeforge_user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      toast.success(`Welcome to CodeForge, ${res.data.user.username}!`, {
        description: "Your hacker journey begins now!",
      });
      return res.data;
    } catch (error) {
      const msg = error.response?.data?.detail || "Registration failed";
      toast.error(msg);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("codeforge_token");
    localStorage.removeItem("codeforge_user");
    setUser(null);
    toast.info("Logged out. See you next time, hacker!");
    // Redirect to home immediately
    window.location.href = "/";
  };

  const refreshUser = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      localStorage.setItem("codeforge_user", JSON.stringify(res.data));
    } catch (error) {
      console.error("Failed to refresh user:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route - Only for logged in users!
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D10] flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl font-bold text-primary mb-4 animate-pulse">
            CODEFORGE
          </div>
          <p className="text-muted-foreground mono">Initializing systems...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App min-h-screen bg-[#0B0D10]">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            
            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sites"
              element={
                <ProtectedRoute>
                  <SitesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sites/:siteId"
              element={
                <ProtectedRoute>
                  <SiteDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sites/:siteId/files"
              element={
                <ProtectedRoute>
                  <FileManagerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ml-studio"
              element={
                <ProtectedRoute>
                  <MLStudioPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tutorials"
              element={
                <ProtectedRoute>
                  <TutorialsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/family"
              element={
                <ProtectedRoute>
                  <FamilyGuidePage />
                </ProtectedRoute>
              }
            />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Toast notifications */}
          <Toaster 
            position="bottom-right"
            toastOptions={{
              style: {
                background: '#16191F',
                border: '1px solid rgba(250, 204, 21, 0.2)',
                color: '#fff',
              },
            }}
          />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
